import { trpcVanilla } from "@client/trpc";
import {
  createSyncFeature,
  type StorageLike,
  type SyncFeature,
} from "@client/sync";
import { type ProgressRow } from "@lib/videoStateTypes";

export interface ProgressPayload {
  videoId: string;
  watchedSeconds: number;
  clientUpdatedAt: string;
  /**
   * Queue-side metadata only (never sent): the round the write was made in,
   * so a consumer draining under a DIFFERENT round's provider can invalidate
   * the source round's cache. The send below maps fields explicitly, which
   * keeps this off the wire by construction.
   */
  sourceRoundId?: string;
}

export interface ProgressFlushResult {
  applied: boolean;
  current: ProgressRow;
}

export function createProgressSync(args: {
  onSuccess: (result: ProgressFlushResult, payload: ProgressPayload) => void;
  storage?: StorageLike;
  // Override for consumers that must not share a queue with the mobile
  // store's worker (two workers over one key double-send the head).
  storageKey?: string;
  // Test seam; production uses the navigator.onLine default.
  isOnline?: () => boolean;
  // See createSyncFeature — commit gate for sends racing an identity flip.
  shouldCommit?: () => boolean;
}): SyncFeature<ProgressPayload> {
  return createSyncFeature<ProgressPayload, ProgressFlushResult>({
    storageKey: args.storageKey ?? "mobileVideoSync.progress.v1",
    coalesce: (existing, incoming) =>
      existing.videoId === incoming.videoId ? incoming : null,
    send: (payload) =>
      trpcVanilla.updateVideoProgress.mutate({
        videoId: payload.videoId,
        watchedSeconds: payload.watchedSeconds,
        clientUpdatedAt: payload.clientUpdatedAt,
      }),
    onSuccess: args.onSuccess,
    storage: args.storage,
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}
